import { contact_admins_path, institution_directory_path, university_path } from "@routes/routes";
import { TShow as UniversityShowResponse } from "@tsTypes/__generated__/mochitypes/universities/show";
import { UniversityMembersOptionsResponse, UniversityMembersResponse } from "@tsTypes/index";
import { ContactAdminsResponse } from "@tsTypes/universities/contact_admins";
import { InstitutionDirectoryResponse } from "@tsTypes/universities/institution_directory";
import appsignal from "@utils/appsignal";
import { SORT_BY_OPTIONS } from "src/views/UniversityTeam/searchOptions";
import request from "./request";
export const getUniversityMembers = async (
  options: {
    facultyFilter: boolean;
    nameFilter: string[];
    titleFilter: string[];
    keywordFilter: string[];
    sortBy: (typeof SORT_BY_OPTIONS)[number]["value"];
  },
  abortSignal: AbortSignal
): Promise<UniversityMembersResponse> => {
  try {
    const response = await request.get("/universities/members", {
      signal: abortSignal,
      params: {
        options: {
          faculty_filter: options.facultyFilter,
          name_filter: options.nameFilter,
          title_filter: options.titleFilter,
          keyword_filter: options.keywordFilter,
          sort_by: options.sortBy ?? "NAME",
        },
      },
    });

    return response.data;
  } catch (e) {
    appsignal.sendError(e, (span) => {
      span.setAction("universities#getUniversityMembers");
    });
    return { members: [], count: 0 };
  }
};

export const getUniversityMembersTabCounts = async (): Promise<{
  faculty_count: number;
  admin_count: number;
}> => {
  try {
    const response = await request.get("/universities/members/tab_counts");

    return response.data;
  } catch (e) {
    appsignal.sendError(e, (span) => {
      span.setAction("universities#getUniversityMembersTabCounts");
    });
    return { faculty_count: 0, admin_count: 0 };
  }
};

export const getUniversityMembersOptions = async (): Promise<UniversityMembersOptionsResponse> => {
  try {
    const response = await request.get("/universities/members/options");

    return response.data;
  } catch (e) {
    appsignal.sendError(e, (span) => {
      span.setAction("universities#getUniversityMembersOptions");
    });
    return {
      name_options: [],
      title_options: [],
      keyword_options: {
        disciplines: [],
        areas_of_expertise: [],
      },
    };
  }
};

export const sendInviteFromUniversityAdmin = async ({
  email,
  message,
}: {
  email: string;
  message: string;
}): Promise<boolean> => {
  try {
    const response = await request.post("/notifications/invite_from_university_admin", {
      to_emails: email,
      message,
    });

    if (!response.data.success) {
      throw new Error("Failed to send invite");
    }

    return true;
  } catch (e) {
    appsignal.sendError(e, (span) => {
      span.setAction("universities#sendInviteFromUniversityAdmin");
    });
    return false;
  }
};

export const sendUniversityAdminInviteToSubmit = async ({
  toUserIds,
  requestIds,
}: {
  toUserIds: number[];
  requestIds: number[];
}): Promise<boolean> => {
  try {
    await request.post("/notifications/university_admin_invite_to_submit", {
      to_user_ids: toUserIds,
      request_ids: requestIds,
    });
    return true;
  } catch (e) {
    appsignal.sendError(e, (span) => {
      span.setAction("universities#sendUniversityAdminInviteToSubmit");
    });
    return false;
  }
};

export const getInstitutionDirectoryUniversities = async ({
  searchFilter = [],
  t1AppliedResearchAreasFilter = [],
  page = 1,
}: {
  searchFilter?: string[];
  t1AppliedResearchAreasFilter?: string[];
  page?: number;
}): Promise<InstitutionDirectoryResponse> => {
  const response = await request.get(institution_directory_path(), {
    params: {
      search_filter: searchFilter,
      t1_applied_research_areas_filter: t1AppliedResearchAreasFilter,
      page,
    },
  });
  return response.data;
};

export const getContactAdmins = async (slug: string): Promise<ContactAdminsResponse> => {
  const response = await request.get(contact_admins_path(slug));
  return response.data;
};

export const getUniversity = async (slug: string): Promise<UniversityShowResponse> => {
  const response = await request.get(university_path(slug));
  return response.data;
};
